import { ItiLink as Link } from 'components';
import dayjs from 'dayjs';
import { FC, MouseEventHandler, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { DataTable, Dialog, Loader, useDialog } from 'components';
import { useAuth } from 'contexts';
import { useResponse } from 'contexts/response';
import { useTransactionApi } from 'hooks/use-transaction-api';
import { getBookingByUnitId, getTransactionById } from 'services';
import { downloadInterchangePdf, getTransactionActivity } from 'services/transactions';
import {
  ActiveUnit,
  Booking,
  Chassis,
  Container,
  EdiCustomerLocation,
  Genset,
  Role,
  Transaction,
  TransactionImage
} from 'types';
import { TransactionActivityDisplay } from 'types/audit/transaction-activity';
import { transformTransactionActivity } from 'utils/transaction-activity';

import SuccessDialog from 'components/organization/dialog/success-dialog';
import { useCustomerAPI } from 'hooks/use-customer-api';
import useTimeZone from 'hooks/use-timezone';
import { ChassisDetails } from './chassis-details';
import { ContainerDetails } from './container-details';
import InspectionSummary from './damages';
import ImageViewer from './image';
import { ResendTransactionEdiDialog } from './resend-transaction-edi-dialog';
import TireDetails from './tires';
import { transactionActivityDataColumns } from './transaction-activity-columns';
import './transaction-details.scss';
import TransactionEvents from './transaction-events';
import TransactionUnits from './transaction-units';

const ViewTransaction: FC = () => {
  const navigate = useNavigate();
  const params = useParams();

  const { userProfile } = useAuth();
  const { getCustomerById } = useCustomerAPI();
  const { closeDialog, openDialog, ref } = useDialog();
  const { error, setError, success } = useResponse();
  const { deleteTransaction } = useTransactionApi();
  const { newZonedDayJs } = useTimeZone();

  const [booking, setBooking] = useState<Booking | null>();
  const [loading, setLoading] = useState<boolean>(false);
  const [transaction, setTransaction] = useState<Transaction | null>(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [ediCustomerLocations, setEdiCustomerLocations] = useState<EdiCustomerLocation[]>([]);
  const [resendEdi, setResendEdi] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');

  /* transaction activities */
  const {
    ref: viewActivityDialogRef,
    openDialog: openViewActivityDialog,
    closeDialog: closeViewActivityDialog
  } = useDialog({});

  const loadTransactionActivities = useCallback(async () => {
    if (!transaction || isNaN(transaction.id)) {
      return [];
    }

    const activities = await getTransactionActivity(transaction.id);
    const transforms: TransactionActivityDisplay[] = [];

    for (const [index, activity] of activities.entries()) {
      transforms.push(
        index > 0
          ? transformTransactionActivity(activity, activities.at(index - 1))
          : transformTransactionActivity(activity)
      );
    }

    return transforms.reverse();
  }, [transaction]);

  const units: ActiveUnit[] = useMemo(() => {
    const list: ActiveUnit[] = [];
    if (transaction) {
      if (!transaction.deleted) {
        list.push(...Object.assign([], transaction[transaction.inbound ? 'gateInUnits' : 'gateOutUnits']));
      } else {
        if (transaction.primaryChassis) list.push(new Chassis(transaction.primaryChassis));
        if (transaction.primaryContainer) list.push(new Container(transaction.primaryContainer));
        if (transaction.primaryGenset) list.push(new Genset(transaction.primaryGenset));
      }
    }
    return list;
  }, [transaction]);

  const redeliveryNumber = useMemo(
    () =>
      units
        .map(u => u.redeliveryNumber)
        .filter(r => r)
        .join(),
    [units]
  );

  const canResendEdi = useMemo(
    () =>
      [
        Role.ADMIN,
        Role.BILLING_ADMIN,
        Role.BOOKING,
        Role.SENIOR_BOOKING,
        Role.ADMIN_CUSTOMER,
        Role.CUSTOMER,
        Role.EXTERNAL
      ].includes(userProfile.role) && !transaction?.deleted,
    [userProfile.role, transaction?.deleted]
  );

  const isUpdateSuccess = useMemo(() => {
    return typeof success === 'string' && success.includes(`Transaction #${transaction?.id} updated.`);
  }, [success, transaction?.id]);

  /* order now preserved by transactionImage.index, where signature.index == 0 */
  /* old transactionImages may not have index, so we assume signature is first */
  const [signature, ...images]: TransactionImage[] = useMemo(() => {
    const transactionImages: TransactionImage[] = Object.assign([], transaction?.images);
    return transactionImages.sort((i0, i1) => (i0.index ?? 1) - (i1.index ?? 1));
  }, [transaction?.images]);

  const loadTransaction = useCallback(async () => {
    const id = Number(params.transactionId);
    if (!isNaN(id)) setTransaction(await getTransactionById(id));
  }, [params.transactionId, setTransaction]);

  useEffect(() => {
    loadTransaction();
  }, [loadTransaction]);

  /* load every EDI recipient of every customer in this transaction */
  useEffect(() => {
    if (!!transaction) {
      const units = transaction.inbound ? transaction.gateInUnits : transaction.gateOutUnits;
      const recipients: EdiCustomerLocation[] = [];
      for (let unit of units) {
        if (!!unit.customer) {
          getCustomerById(unit.customer.id).then(customer => {
            if (!!customer) {
              recipients.push(...customer.ediRecipients.filter(r1 => recipients.every(r2 => r1.id !== r2.id)));
            }
          });
        }
      }
      setEdiCustomerLocations(recipients);
    }
  }, [getCustomerById, transaction]);

  useEffect(() => {
    if (transaction?.deleted || (transaction && !transaction.primaryUnit)) {
      setBooking(null);
      return;
    } else if (transaction?.primaryUnit?.id) {
      getBookingByUnitId(transaction.primaryUnit.id).then(setBooking);
    }
  }, [setBooking, transaction]);

  const [notesClass, setNotesClass] = useState('notes text-ellipsis');
  const toggleNotes = useCallback(() => {
    if (!notesClass) return;
    setNotesClass(p => (!p.includes('text-ellipsis') ? 'notes text-ellipsis' : 'notes'));
  }, [notesClass, setNotesClass]);
  /* disable hover effect for short notes */
  const handleHover: MouseEventHandler<HTMLParagraphElement> = useCallback(
    e =>
      notesClass &&
      notesClass !== 'notes' &&
      e.target instanceof HTMLParagraphElement &&
      e.target.offsetWidth >= e.target.scrollWidth &&
      setNotesClass(''),
    [notesClass, setNotesClass]
  );

  const handleDelete = useCallback(async () => {
    if (transaction?.id && deleteReason.trim()) {
      setLoading(true);
      const response = await deleteTransaction(transaction.id, deleteReason.trim());
      if (response) {
        navigate('/transactions', { replace: true });
      }
      closeDialog();
      setDeleteReason('');
      setLoading(false);
    }
  }, [transaction?.id, deleteReason, closeDialog, deleteTransaction, navigate, setLoading]);

  const handleCloseDialog = useCallback(async () => {
    if (!loading) {
      closeDialog();
      setDeleteReason('');
      setError('');
    }
  }, [loading, closeDialog, setError]);

  const handlePdfDownload = (id: number) => {
    setLoadingPdf(true);
    downloadInterchangePdf(id).then(() => setLoadingPdf(false));
  };

  if (!transaction) return null;

  return (
    <div className="page" id="transaction-details">
      <div className="page-header">
        <div className="d-flex g-3 margin-right-16px">
          <h1>TRANSACTION #{transaction.id}</h1>
          {!transaction.inbound ? (
            <label className="status gate-out">GATE OUT</label>
          ) : (
            <label className="status gate-in">GATE IN</label>
          )}
          {transaction.bulk && <label className="status bulk">BULK</label>}
          {transaction.deleted && <label className="status deleted">DELETED</label>}
          {!dayjs(transaction.createdDate).isSame(dayjs(transaction.completedDate), 'hour') && (
            <>
              <label className="status backdated">
                BACKDATED
                <div className="backdated-tooltip">
                  Date Created: {newZonedDayJs(transaction.createdDate).format('l LT')}
                </div>
              </label>
            </>
          )}
        </div>
        <div className="d-flex g-3 flex-row-reverse">
          {[Role.ADMIN, Role.BILLING_ADMIN, Role.SENIOR_BOOKING, Role.BOOKING].includes(userProfile.role) && (
            <button
              className="btn-primary text-uppercase"
              onClick={openViewActivityDialog}
              data-cy="transaction-view-log-button"
            >
              View Log
            </button>
          )}
          {[Role.ADMIN, Role.BILLING_ADMIN, Role.SENIOR_BOOKING, Role.GATE_BOOKING].includes(userProfile.role) &&
            !transaction.deleted && (
            <button
              className="btn-primary"
              onClick={() => navigate('edit', { replace: true })}
              data-cy="transaction-edit-button"
            >
              EDIT
            </button>
          )}
          {[Role.ADMIN, Role.BILLING_ADMIN, Role.SENIOR_BOOKING].includes(userProfile.role) && !transaction.deleted && (
            <button className="btn-danger" onClick={openDialog} data-cy="transaction-delete-button">
              DELETE
            </button>
          )}
          {[
            Role.ADMIN,
            Role.BILLING_ADMIN,
            Role.SENIOR_BOOKING,
            Role.BOOKING,
            Role.CARRIER,
            Role.ADMIN_CUSTOMER,
            Role.CUSTOMER,
            Role.EXTERNAL
          ].includes(userProfile.role) && (
            <Loader loading={loadingPdf}>
              <button
                className="btn-outline"
                onClick={() => handlePdfDownload(transaction.id)}
                data-cy="transaction-download-button"
              >
                DOWNLOAD
              </button>
            </Loader>
          )}
          {canResendEdi && (
            <button
              className="btn-outline"
              onClick={() => setResendEdi(true)}
              data-cy="transaction-resend-edi-button"
            >
              RESEND EDI
            </button>
          )}
          <button className="btn-back" onClick={() => navigate(-1)}>
            <span className="material-icons">arrow_back_ios</span>
            BACK
          </button>
        </div>
      </div>
      <Dialog width="80vw" ref={viewActivityDialogRef} closeDialog={closeViewActivityDialog}>
        <div className="dialog-header">
          <h3>Activity Log</h3>
          <button className="btn-close" onClick={closeViewActivityDialog}>
            <span className="material-icons">close</span>
          </button>
        </div>
        <div className="dialog-body">
          <DataTable
            tableClass="activity-log"
            fetchData={loadTransactionActivities}
            columns={transactionActivityDataColumns}
            initialPage={{ pageNum: 0, pageSize: 25, sortField: 'date', sortDirection: 'desc' }}
            initialColumns={transactionActivityDataColumns.map(({ field, width }) => {
              return { field, width };
            })}
            clientSidePagination
            hideTableControls
          />
        </div>
      </Dialog>
      <div className="page-content">
        <TransactionUnits units={!transaction.deleted ? undefined : units} />
        <ContainerDetails units={units} />
        <ChassisDetails units={units} />
        <div className="card-primary">
          <div className="card-header px-3">
            <h4>TRUCKING DETAILS</h4>
          </div>
          <div className="card-body">
            <div className="grid-auto p-2">
              <div>
                <h6 className="text-dark font-weight-regular">COMPANY (SCAC)</h6>
                <div className="text-black row g-1">
                  <p className="flex-shrink-1 nowrap" data-cy="trucking-company-name">
                    {transaction.truckingCompany?.name ?? 'N/A'}
                  </p>
                  <p className="flex-shrink-0" data-cy="trucking-company-scac">
                    ({transaction.truckingCompany?.scacCode ?? 'N/A'})
                  </p>
                </div>
              </div>
              <div>
                <h6 className="text-dark font-weight-regular">TRUCK NUMBER</h6>
                <p className="text-black" data-cy="truck-number">
                  {transaction.truckNumber ?? 'N/A'}
                </p>
              </div>
              <div>
                <h6 className="text-dark font-weight-regular">DRIVER NAME</h6>
                <p className="text-black" data-cy="driver-name">
                  {transaction.driverName ?? 'N/A'}
                </p>
              </div>
              <div>
                <h6 className="text-dark font-weight-regular">CDL NUMBER</h6>
                <p className="text-black" data-cy="cdl-number">
                  {transaction.cdlNumber ?? 'N/A'}
                </p>
              </div>
              <div>
                <h6 className="text-dark font-weight-regular">LICENSE PLATE</h6>
                <p className="text-black" data-cy="license-plate">
                  {transaction.licensePlate ?? 'N/A'}
                </p>
              </div>
              <div>
                <h6 className="text-dark font-weight-regular">SIGNATURE</h6>
                <ImageViewer images={[signature]}>VIEW SIGNATURE</ImageViewer>
              </div>
            </div>
          </div>
        </div>
        <div className="card-primary">
          <div className="card-header px-3">
            <h4>GENERAL INFORMATION</h4>
          </div>
          <div className="card-body">
            <div className="grid-auto p-2">
              <div>
                <h6 className="text-dark font-weight-regular">TYPE</h6>
                <p className="text-black" data-cy="transaction-type">
                  {!transaction.inbound ? 'GATE OUT' : 'GATE IN'}
                </p>
              </div>
              <div>
                <h6 className="text-dark font-weight-regular">BOOKING NUMBER</h6>
                <Loader loading={booking === undefined}>
                  {!!booking &&
                  [
                    Role.ADMIN,
                    Role.BILLING_ADMIN,
                    Role.BOOKING,
                    Role.SENIOR_BOOKING,
                    Role.ADMIN_CUSTOMER,
                    Role.CUSTOMER,
                    Role.EXTERNAL
                  ].includes(userProfile.role) &&
                  userProfile.customers?.some(({ id }) => id === booking.customer?.id) ? (
                    <Link to={`/bookings/${booking.id}`} data-cy="transaction-booking-link">
                      {booking.bookingNumber}
                    </Link>
                  ) : (
                    <p className="text-black" data-cy="transaction-booking">
                      {booking?.bookingNumber ?? 'N/A'}
                    </p>
                  )}
                </Loader>
              </div>
              <div>
                <h6 className="text-dark font-weight-regular">REDELIVERY NUMBER</h6>
                <p className="text-black" data-cy="transaction-redelivery-number">
                  {redeliveryNumber ?? 'N/A'}
                </p>
              </div>
              <div>
                <h6 className="text-dark font-weight-regular">NOTES</h6>
                <p
                  key={notesClass}
                  className={notesClass}
                  onClick={toggleNotes}
                  onMouseEnter={handleHover}
                  data-cy="transaction-notes"
                >
                  {transaction.notes ?? 'N/A'}
                </p>
              </div>
              {[Role.ADMIN, Role.BILLING_ADMIN, Role.BOOKING].includes(userProfile.role) && (
                <>
                  <div>
                    <h6 className="text-dark font-weight-regular">GATE USER</h6>
                    <p className="text-black" data-cy="transaction-gate-user">
                      {transaction.completedBy ?? transaction.createdBy?.replace(/@.+/, '')}
                    </p>
                  </div>
                  <div>
                    <h6 className="text-dark font-weight-regular">BOOKING CREATED BY</h6>
                    <p className="text-black" data-cy="transaction-booking-user">
                      {booking?.createdBy ?? 'N/A'}
                    </p>
                  </div>
                </>
              )}
              <div>
                <h6 className="text-dark font-weight-regular">TRANSACTION IMAGES</h6>
                <ImageViewer images={images} />
              </div>
              <div>
                <h6 className="text-dark font-weight-regular">TIRE IMAGES</h6>
                <ImageViewer
                  images={transaction.tires?.filter(t => t.imageBase64)}
                  getTitle={tire => `${tire.axle} ${tire.side} - ${tire.centrality}`}
                />
              </div>
            </div>
          </div>
        </div>
        <InspectionSummary {...transaction} />
        <TireDetails tires={transaction.tires} />
        {[Role.ADMIN, Role.BILLING_ADMIN].includes(userProfile.role) && <TransactionEvents />}
      </div>
      {!!resendEdi && (
        <ResendTransactionEdiDialog
          close={() => setResendEdi(false)}
          transaction={transaction}
          ediCustomerLocations={ediCustomerLocations.filter(ecl => ecl.ediType === 'GATE')}
        />
      )}
      <Dialog closeDialog={handleCloseDialog} ref={ref}>
        {!error ? (
          <Loader loading={loading}>
            <div className="d-flex flex-column g-3">
              <h3 className="text-info-dark font-weight-medium">Delete Transaction</h3>
              <p className="text-info-main">Discard transaction {transaction?.id} and return to transactions list?</p>
              <label htmlFor="delete-reason">Please add a reason: </label>
              <textarea
                id="delete-reason"
                value={deleteReason}
                onChange={e => setDeleteReason(e.target.value)}
                rows={3}
                maxLength={300}
                required
                data-cy="transaction-delete-reason"
              />
              <div className="grid-3 mt-3">
                <button className="btn-outline col-start-2" onClick={handleCloseDialog}>
                  close
                </button>
                <button
                  className="btn-danger col-start-3"
                  onClick={handleDelete}
                  disabled={!deleteReason.trim()}
                  data-cy="transaction-delete-confirm"
                >
                  confirm
                </button>
              </div>
            </div>
          </Loader>
        ) : (
          <div className="d-flex flex-column g-3">
            <h3 className="text-info-dark font-weight-medium">Delete Transaction Failed</h3>
            <p className="text-info-main">{error}</p>
            <div className="grid-3 mt-3">
              <button className="btn-outline col-start-3" onClick={handleCloseDialog}>
                close
              </button>
            </div>
          </div>
        )}
      </Dialog>
      <SuccessDialog
        bodyText={
          isUpdateSuccess && canResendEdi ? (
            <div className="text-center">
              <div>{success}</div>
              <div className="font-weight-bold mt-3">Would you like to resend the EDI?</div>
            </div>
          ) : undefined
        }
        actionButtonLabel={isUpdateSuccess && canResendEdi ? 'YES' : undefined}
        closeButtonLabel={isUpdateSuccess && canResendEdi ? 'NO' : undefined}
        onActionClick={isUpdateSuccess && canResendEdi ? () => setResendEdi(true) : undefined}
        width="min(90vw, 450px)"
      />
    </div>
  );
};

export default ViewTransaction;
